import React, { useEffect, useState, useMemo, useRef } from 'react';
import apiClient from '../config/api';

const Noticias = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  const getImages = (item) => {
    if (!item) return [];
    if (Array.isArray(item.images) && item.images.length > 0) {
      return item.images.map(i => i.url).filter(Boolean);
    }
    return item.url ? [item.url] : [];
  };

  const categories = [
    { id: 'todos', name: 'Todas', icon: 'ph-grid-four' },
    { id: 'geral', name: 'Geral', icon: 'ph-newspaper' },
    { id: 'comunicados', name: 'Comunicados', icon: 'ph-megaphone' },
    { id: 'eventos', name: 'Eventos', icon: 'ph-calendar-check' },
    { id: 'promocoes', name: 'Promoções', icon: 'ph-percent' },
    { id: 'outros', name: 'Outros', icon: 'ph-dots-three-outline' }
  ];

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/news');
        const data = response?.data?.images || response?.data || [];
        setItems(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Erro ao carregar notícias:', error);
        // Fallback sample news
        setItems([
          { _id: '1', titulo: 'Bem-vindo ao Bondegusto', descricao: 'Agora temos um espaço dedicado às notícias!', categoria: 'geral', url: '', ativo: true },
          { _id: '2', titulo: 'Promoção da Semana', descricao: 'Descontos especiais em pratos selecionados.', categoria: 'promocoes', url: '', ativo: true },
          { _id: '3', titulo: 'Evento de Degustação', descricao: 'Participe do nosso evento com chefs convidados.', categoria: 'eventos', url: '', ativo: true }
        ]);
      } finally {
        setLoading(false);
      }
    };
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchNews();
  }, []);

  const filteredItems = useMemo(() => {
    const active = items.filter(i => i.isActive !== false);
    return selectedCategory === 'todos' ? active : active.filter(i => i.categoria === selectedCategory);
  }, [items, selectedCategory]);

  const sortedFilteredItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const aDate = new Date(a.createdAt || 0).getTime();
      const bDate = new Date(b.createdAt || 0).getTime();
      return bDate - aDate; // newest first
    });
  }, [filteredItems]);

  const openDetails = async (item) => {
    setDetailsLoading(true);
    setActiveImageIdx(0);
    try {
      const response = await apiClient.get(`/news/${item._id}`);
      const full = response?.data?.image || response?.data || item;
      setSelectedItem(full);
    } catch (error) {
      console.error('Erro ao carregar detalhes da notícia:', error);
      // Fallback para dados já carregados
      setSelectedItem(item);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => { setSelectedItem(null); setActiveImageIdx(0); };
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-800 dark:text-white mb-4">Notícias</h1>
        <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">Acompanhe as últimas novidades, comunicados e eventos</p>
      </div>

      {/* Category Filter */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap gap-3 justify-center">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all ${
                selectedCategory === category.id
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
              }`}
            >
              <i className={`ph ${category.icon} mr-2`}></i>
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, idx) => (
            <div key={idx} className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4 animate-pulse">
              <div className="h-40 bg-slate-200 dark:bg-slate-700 rounded-lg mb-3"></div>
              <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
            </div>
          ))}
        </div>
      )}

      {/* News Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedFilteredItems.map(item => {
            const itemImages = getImages(item);
            const coverUrl = itemImages[0];
            return (
            <article
              key={item._id}
              className="group bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden cursor-pointer hover:shadow-2xl hover:-translate-y-1 ring-1 ring-transparent hover:ring-primary/30 transition-all duration-300 ease-out"
              onClick={() => openDetails(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openDetails(item); }}
              aria-label={`Abrir detalhes da notícia: ${item.titulo}`}
            >
              <div className="relative overflow-hidden">
                {coverUrl ? (
                  <img
                    src={coverUrl}
                    alt={item.titulo}
                    className="w-full h-48 object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-48 bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                    <i className="ph ph-newspaper text-4xl text-slate-400"></i>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {itemImages.length > 1 && (
                  <span className="absolute top-2 right-2 px-2 py-1 text-xs font-medium bg-black/60 text-white rounded-full flex items-center">
                    <i className="ph ph-images mr-1"></i>
                    {itemImages.length}
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-slate-800 dark:text-white group-hover:text-primary transition-colors duration-300">{item.titulo}</h3>
                <div className="mt-3 flex items-center text-sm text-slate-500 dark:text-slate-400">
                  <i className="ph ph-tag mr-1"></i>
                  <span className="capitalize">{item.categoria || 'geral'}</span>
                  {item.createdAt && (
                    <span className="ml-auto"><i className="ph ph-calendar-blank mr-1"></i>{formatDate(item.createdAt)}</span>
                  )}
                </div>
              </div>
            </article>
            );
          })}
        </div>
      )}

      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={closeDetails}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
              <h3 className="text-xl font-semibold text-slate-800 dark:text-white pr-2">{selectedItem.titulo}</h3>
              <button onClick={closeDetails} className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white flex-shrink-0" aria-label="Fechar detalhes">
                <i className="ph ph-x text-2xl"></i>
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {(() => {
                const modalImages = getImages(selectedItem);
                const safeIdx = Math.min(activeImageIdx, Math.max(0, modalImages.length - 1));
                const currentUrl = modalImages[safeIdx];
                const hasMultiple = modalImages.length > 1;
                return (
                  <div className="space-y-3">
                    <div className="relative rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 flex items-center justify-center h-56 sm:h-72 md:h-80">
                      {currentUrl ? (
                        <img src={currentUrl} alt={`${selectedItem.titulo} ${safeIdx + 1}`} className="max-w-full max-h-full object-contain" />
                      ) : (
                        <div className="flex items-center justify-center">
                          <i className="ph ph-newspaper text-5xl text-slate-400"></i>
                        </div>
                      )}
                      {hasMultiple && (
                        <>
                          <button
                            onClick={() => setActiveImageIdx((safeIdx - 1 + modalImages.length) % modalImages.length)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                            aria-label="Imagem anterior"
                          >
                            <i className="ph ph-caret-left text-lg"></i>
                          </button>
                          <button
                            onClick={() => setActiveImageIdx((safeIdx + 1) % modalImages.length)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                            aria-label="Próxima imagem"
                          >
                            <i className="ph ph-caret-right text-lg"></i>
                          </button>
                          <span className="absolute bottom-2 right-2 px-2 py-1 text-xs bg-black/60 text-white rounded-full">
                            {safeIdx + 1} / {modalImages.length}
                          </span>
                        </>
                      )}
                    </div>
                    {hasMultiple && (
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {modalImages.map((url, idx) => (
                          <button
                            key={url + idx}
                            onClick={() => setActiveImageIdx(idx)}
                            className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                              idx === safeIdx ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'
                            }`}
                            aria-label={`Ver imagem ${idx + 1}`}
                          >
                            <img src={url} alt={`Miniatura ${idx + 1}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="flex items-center text-sm text-slate-600 dark:text-slate-300">
                <i className="ph ph-tag mr-1"></i>
                <span className="capitalize">{selectedItem.categoria || 'geral'}</span>
                {selectedItem.createdAt && (
                  <span className="ml-auto"><i className="ph ph-calendar-blank mr-1"></i>{formatDate(selectedItem.createdAt)}</span>
                )}
              </div>
              {detailsLoading ? (
                <div className="text-center py-6 text-slate-500 dark:text-slate-300">Carregando detalhes...</div>
              ) : (
                selectedItem.descricao ? (
                  <p className="text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{selectedItem.descricao}</p>
                ) : (
                  <p className="text-slate-500 dark:text-slate-300">Esta notícia não possui conteúdo adicional.</p>
                )
              )}
              <div className="flex justify-end">
                <button onClick={closeDetails} className="px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90">Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredItems.length === 0 && (
        <div className="text-center py-12">
          <i className="ph ph-newspaper text-6xl text-slate-400 dark:text-slate-600 mb-4"></i>
          <h3 className="text-xl font-semibold text-slate-600 dark:text-slate-400 mb-2">Nenhuma notícia encontrada</h3>
          <p className="text-slate-500 dark:text-slate-500">Tente selecionar outra categoria</p>
        </div>
      )}
    </div>
  );
};

export default Noticias;